import React, { useEffect, useRef, useState } from 'react';
import {
  ArrowDown,
  ArrowUp,
  Download,
  HelpCircle,
  Pencil,
  Printer,
  Search,
  Trash2,
  Undo2,
} from 'lucide-react';
import BoothSheet from '../reflect/BoothSheet';
import LineField from '../reflect/LineField';
import RhymeBrain from '../reflect/RhymeBrain';
import {
  groupRhymeFamilies,
  hookNeedsRhymeTip,
  lineEndingWord,
  missingSectionLabels,
  pocketMeter,
  swapLastWord,
  totalLineCount,
} from '../../utils/reflectEditor';
import { vibeStyle } from '../../utils/vibeStyle';
import { GUIDE_STEPS, filledLines } from '../../utils/boothGuide';
import { LocalStarters } from './GuidePanel';
import SendToArranger from './SendToArranger';

export const BOOTH_SECTIONS = [
  'Intro',
  'Hook',
  'Verse 1',
  'Verse 2',
  'Verse 3',
  'Bridge',
  'Outro',
];

const SECTION_EXPLAINERS = {
  Intro: 'Sets the mood before the words hit. Optional. 1 to 4 lines.',
  Hook: 'The part people remember and sing back. 2 to 4 lines, repeated.',
  Bridge: 'A switch-up before the last hook. Different angle, 2 to 4 lines.',
  Outro: 'How the song lands. 1 to 4 lines.',
};

const sectionExplainer = (label) => (
  /^Verse [123]$/.test(label)
    ? 'Tells the story. 4 to 8 lines, one per bar.'
    : SECTION_EXPLAINERS[label]
);

const POCKET_LEGEND = 'Each dot is a syllable. 16 fills a bar at a normal rap pace. '
  + 'Under 16 is fine. Over 16 gets crowded.';

const sheetWords = (sheet) => {
  const unique = new Set();
  (sheet.sections || []).forEach((section) => {
    (section.lines || []).forEach((line) => {
      const words = String(line.text || '').match(/[\p{L}\p{N}]+(?:['’][\p{L}\p{N}]+)*/gu) || [];
      words.forEach((word) => {
        if (word.length <= 30 && unique.size < 400) unique.add(word.toLocaleLowerCase());
      });
    });
  });
  return [...unique];
};

// The step's instructions sit inside the section being written, so they stay in view after the
// guide scrolls the writer down to it.
const GuideCallout = ({ aiAvailable, color, complete, onNext, section, step }) => {
  const info = GUIDE_STEPS[step];
  const needed = step === 3 ? 2 : 4;
  const have = Math.min(needed, filledLines(section));
  return (
    <div className="guide-callout mb-5 rounded-2xl border p-4"
      style={{ borderColor: `${color}55`, background: `${color}12` }}>
      <p className="text-xs font-black uppercase tracking-[0.18em]" style={{ color }}>
        Step {step} of 5
      </p>
      <p className="mt-1 text-base font-bold leading-relaxed text-white/85">{info.text}</p>
      {info.tip && <p className="mt-2 text-base leading-relaxed text-white/60">{info.tip}</p>}
      {!aiAvailable && <LocalStarters step={step} />}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <span className="text-sm font-black text-white/55">{have} of {needed} lines written</span>
        <button
          type="button"
          onClick={onNext}
          disabled={!complete}
          className="min-h-11 rounded-full px-5 text-base font-black text-[#0B0B0C] disabled:opacity-35"
          style={{ background: color }}
        >
          Next
        </button>
      </div>
    </div>
  );
};

const BoothEditor = ({
  PizzulMoves = null,
  dispatch,
  guideStep = null,
  guideCallout = null,
  onDownload,
  onTip,
  pizzulMovesProps = {},
  requestRhymes,
  sheet,
}) => {
  const [titleEditing, setTitleEditing] = useState(false);
  const [focusedLine, setFocusedLine] = useState(null);
  const [lookupTrigger, setLookupTrigger] = useState(null);
  const [deletedLine, setDeletedLine] = useState(null);
  const [openExplainer, setOpenExplainer] = useState(null);
  const [pocketHelp, setPocketHelp] = useState(null);
  const deletedTimer = useRef(null);
  const sectionRefs = useRef({});
  const triggerId = useRef(0);
  const sheetRef = useRef(sheet);
  const { color } = vibeStyle(sheet.vibe || '');

  sheetRef.current = sheet;

  useEffect(() => () => clearTimeout(deletedTimer.current), []);

  useEffect(() => {
    const label = guideStep === 3 ? 'Hook' : guideStep === 4 ? 'Verse 1' : null;
    if (!label) return;
    const section = sectionRefs.current[label];
    const field = section?.querySelector('textarea');
    if (!field) return;
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    // Land on the section's top so the step instructions inside it are in view.
    section.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
    field.focus({ preventScroll: true });
  }, [guideStep]);

  useEffect(() => {
    const crowded = (sheet.sections || []).some((section) => (
      (section.lines || []).some((line) => pocketMeter(line.text).crowded)
    ));
    onTip('crowded', crowded);
  }, [onTip, sheet.sections]);

  useEffect(() => {
    onTip('hook_rhyme', hookNeedsRhymeTip(sheet.sections));
  }, [onTip, sheet.sections]);

  const removeLine = (section, line, value) => {
    dispatch({ type: 'remove_line', section, line });
    setDeletedLine({ section, line, value });
    clearTimeout(deletedTimer.current);
    deletedTimer.current = window.setTimeout(() => setDeletedLine(null), 6000);
  };

  const undoDelete = () => {
    if (!deletedLine) return;
    dispatch({ type: 'restore_line', ...deletedLine });
    setDeletedLine(null);
    clearTimeout(deletedTimer.current);
  };

  const lookupLine = (section, line, text) => {
    const word = lineEndingWord(text);
    if (!word) return;
    setFocusedLine({ section, line });
    triggerId.current += 1;
    setLookupTrigger({ id: triggerId.current, word });
  };

  const swapFocusedLine = (word) => {
    if (!focusedLine) return;
    const line = sheetRef.current.sections[focusedLine.section]?.lines[focusedLine.line];
    if (!line) return;
    dispatch({
      type: 'edit_line',
      section: focusedLine.section,
      line: focusedLine.line,
      text: swapLastWord(line.text, word),
    });
  };

  const lookupRhymes = (word) => requestRhymes(word, sheetWords(sheetRef.current));
  const canAddLines = totalLineCount(sheet.sections) < 48;
  const missing = missingSectionLabels(sheet.sections, BOOTH_SECTIONS);

  return (
    <div
      className={`booth-editor guide-step-${guideStep || 'off'} mx-auto w-full max-w-7xl pb-24`}
      style={{ '--glow': color }}
    >
      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_21rem]">
        <main className="min-w-0">
          <div className="booth-print-sheet mb-8 text-center">
            {titleEditing ? (
              <input
                value={sheet.title}
                onChange={(event) => dispatch({ type: 'edit_title', title: event.target.value })}
                onBlur={() => setTitleEditing(false)}
                maxLength={80}
                autoFocus
                className="min-h-14 w-full rounded-2xl border border-white/15 bg-black/25 px-4
                  text-center font-heading text-4xl font-black text-white outline-none
                  focus:border-[color:var(--glow)] sm:text-6xl"
                aria-label="Song title"
              />
            ) : (
              <button
                type="button"
                onClick={() => setTitleEditing(true)}
                className="group inline-flex min-h-14 max-w-full items-center gap-3 rounded-2xl px-3
                  font-heading text-4xl font-black focus:outline-none focus-visible:ring-2
                  focus-visible:ring-[color:var(--glow)] sm:text-6xl"
              >
                <span className="truncate">{sheet.title}</span>
                <Pencil className="h-5 w-5 shrink-0 text-white/35 group-hover:text-white" />
              </button>
            )}
            <p className="booth-print-style mt-3 text-base font-bold text-white/55">{sheet.style}</p>
          </div>

          <div className="booth-print-lyrics space-y-5">
            {PizzulMoves && !focusedLine && (
              <PizzulMoves
                focusedLine={focusedLine}
                sheet={sheet}
                dispatch={dispatch}
                color={color}
                {...pizzulMovesProps}
              />
            )}
            {(sheet.sections || []).map((section, sectionIndex) => {
              const rhymeFamilies = groupRhymeFamilies(section.lines);
              return (
                <section
                  key={section.label}
                  ref={(node) => { sectionRefs.current[section.label] = node; }}
                  className={`booth-song-section overflow-hidden rounded-[1.75rem] border
                    border-white/10 bg-white/[0.035] p-4 sm:p-6 ${
                    (guideStep === 3 && section.label === 'Hook')
                    || (guideStep === 4 && section.label === 'Verse 1') ? 'guide-target' : ''}`}
                >
                  {guideCallout && ((guideStep === 3 && section.label === 'Hook')
                    || (guideStep === 4 && section.label === 'Verse 1')) && (
                    <GuideCallout color={color} step={guideStep} section={section} {...guideCallout} />
                  )}
                  <div className="mb-4 flex flex-wrap items-center gap-2">
                    <h2
                      className="font-heading text-xl font-black uppercase tracking-[0.12em]"
                      style={{ color }}
                    >
                      {section.label}
                    </h2>
                    <button
                      type="button"
                      onClick={() => setOpenExplainer((current) => (
                        current === section.label ? null : section.label
                      ))}
                      className="booth-help-button flex min-h-11 min-w-11 items-center
                        justify-center rounded-full text-white/50 hover:bg-white/10 hover:text-white"
                      aria-label={`What is ${section.label}?`}
                      aria-expanded={openExplainer === section.label}
                    >
                      <HelpCircle className="h-4 w-4" />
                    </button>
                    {openExplainer === section.label && (
                      <p className="booth-inline-help w-full rounded-xl border border-white/10
                        bg-black/25 p-3 text-base font-bold normal-case tracking-normal text-white/70"
                      >
                        {sectionExplainer(section.label)}
                      </p>
                    )}
                  </div>
                  <div className="space-y-3">
                    {(section.lines || []).map((line, lineIndex) => {
                      const meter = pocketMeter(line.text);
                      const family = rhymeFamilies[lineIndex];
                      const isFocused = focusedLine?.section === sectionIndex
                        && focusedLine?.line === lineIndex;
                      return (
                        <div
                          key={`${section.label}-${lineIndex}`}
                          className="booth-line rounded-2xl border border-white/10 bg-black/20 p-3"
                        >
                          <div className="flex items-center gap-2">
                            <LineField
                              value={line.text}
                              onChange={(event) => dispatch({
                                type: 'edit_line',
                                section: sectionIndex,
                                line: lineIndex,
                                text: event.target.value,
                              })}
                              onFocus={() => setFocusedLine({
                                section: sectionIndex,
                                line: lineIndex,
                              })}
                              maxLength={140}
                              placeholder={section.label === 'Hook'
                                ? 'Start your hook' : 'Start your line'}
                              className="min-h-12 min-w-0 flex-1 resize-none overflow-hidden
                                border-0 bg-transparent px-2 py-2.5 text-base font-bold
                                leading-relaxed text-white outline-none placeholder:text-white/30
                                sm:text-lg"
                              aria-label={`${section.label} line ${lineIndex + 1}`}
                            />
                            <button
                              type="button"
                              onClick={() => lookupLine(sectionIndex, lineIndex, line.text)}
                              disabled={!lineEndingWord(line.text)}
                              className="booth-editor-control flex min-h-11 min-w-11 items-center
                                justify-center rounded-full border border-white/10 text-white/60
                                hover:border-[color:var(--glow)] hover:text-white disabled:opacity-30"
                              aria-label={`Find rhymes for ${lineEndingWord(line.text) || 'this line'}`}
                            >
                              <Search className="h-4 w-4" />
                            </button>
                            <span
                              className="booth-rhyme-dot h-3 w-3 shrink-0 rounded-full"
                              aria-label={family.key
                                ? `Rhyme family ${family.key}` : 'No rhyme family'}
                              style={{
                                backgroundColor: family.key ? color : 'rgba(255,255,255,0.16)',
                                opacity: family.key ? Math.max(0.45, 1 - family.family * 0.12) : 1,
                                boxShadow: family.key ? `0 0 8px ${color}` : 'none',
                              }}
                            />
                          </div>

                          <div className="booth-line-tools mt-2 flex flex-wrap items-center
                            justify-between gap-2 pl-2 sm:pl-5"
                          >
                            <div className="flex flex-wrap items-center gap-2">
                              <span className={`text-base font-black ${meter.crowded
                                ? 'text-[#FFB347]' : 'text-white/55'}`}
                              >
                                {meter.count} syllables{meter.crowded ? ' · crowded' : ''}
                              </span>
                              {isFocused && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    const key = `${sectionIndex}-${lineIndex}`;
                                    setPocketHelp((current) => current === key ? null : key);
                                  }}
                                  className="booth-help-button flex min-h-11 min-w-11 items-center
                                    justify-center rounded-full text-white/50 hover:bg-white/10
                                    hover:text-white"
                                  aria-label="Explain the syllable dots"
                                  aria-expanded={pocketHelp === `${sectionIndex}-${lineIndex}`}
                                >
                                  <HelpCircle className="h-4 w-4" />
                                </button>
                              )}
                              <span
                                className="flex gap-0.5"
                                aria-label={`${meter.filled} of 16 notes filled`}
                              >
                                {Array.from({ length: 16 }).map((_, dot) => (
                                  <span
                                    key={dot}
                                    className="h-1.5 w-1.5 rounded-full"
                                    style={{
                                      backgroundColor: dot < meter.filled
                                        ? (meter.crowded ? '#FFB347' : color)
                                        : 'rgba(255,255,255,0.14)',
                                    }}
                                  />
                                ))}
                              </span>
                            </div>
                            <div className="flex gap-1">
                              <button
                                type="button"
                                onClick={() => dispatch({
                                  type: 'move_line',
                                  section: sectionIndex,
                                  line: lineIndex,
                                  direction: -1,
                                })}
                                disabled={lineIndex === 0}
                                className="flex min-h-11 min-w-11 items-center justify-center
                                  rounded-full text-white/55 hover:bg-white/10 hover:text-white
                                  disabled:opacity-25"
                                aria-label="Move line up"
                              >
                                <ArrowUp className="h-4 w-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => dispatch({
                                  type: 'move_line',
                                  section: sectionIndex,
                                  line: lineIndex,
                                  direction: 1,
                                })}
                                disabled={lineIndex === section.lines.length - 1}
                                className="flex min-h-11 min-w-11 items-center justify-center
                                  rounded-full text-white/55 hover:bg-white/10 hover:text-white
                                  disabled:opacity-25"
                                aria-label="Move line down"
                              >
                                <ArrowDown className="h-4 w-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => removeLine(sectionIndex, lineIndex, line)}
                                className="flex min-h-11 min-w-11 items-center justify-center
                                  rounded-full text-white/55 hover:bg-red-500/10 hover:text-red-200"
                                aria-label="Delete line"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                          {isFocused && pocketHelp === `${sectionIndex}-${lineIndex}` && (
                            <p className="booth-inline-help mt-2 rounded-xl border border-white/10
                              bg-black/25 p-3 text-base font-bold leading-relaxed text-white/70"
                            >
                              {POCKET_LEGEND}
                            </p>
                          )}
                          {PizzulMoves
                            && focusedLine?.section === sectionIndex
                            && focusedLine?.line === lineIndex && (
                              <PizzulMoves
                                focusedLine={focusedLine}
                                sheet={sheet}
                                dispatch={dispatch}
                                color={color}
                                {...pizzulMovesProps}
                              />
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <button
                    type="button"
                    onClick={() => dispatch({ type: 'add_line', section: sectionIndex })}
                    disabled={!canAddLines || section.lines.length >= 16}
                    className="booth-editor-control mt-3 min-h-12 rounded-full border border-dashed
                      border-white/20 px-5 text-base font-black text-white/65
                      hover:border-[color:var(--glow)] hover:text-white disabled:opacity-35"
                  >
                    + Add line
                  </button>
                </section>
              );
            })}
          </div>

          {missing.length > 0 && (
            <div className="booth-editor-control mt-5 flex flex-wrap gap-2">
              {missing.map((label) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => dispatch({
                    type: 'add_section',
                    label,
                    order: BOOTH_SECTIONS,
                  })}
                  className="min-h-12 rounded-full border border-white/15 px-5 text-base
                    font-black hover:border-[color:var(--glow)] hover:bg-white/[0.04]"
                >
                  + Add {label}
                </button>
              ))}
            </div>
          )}

          <div className="booth-print-flow">
            <BoothSheet
              cleanRhymes={false}
              draft={sheet}
              kicker="Ready to record"
              onChange={dispatch}
              onFirstCopy={() => onTip('first_copy')}
            />
          </div>

          <div className="booth-editor-control mt-5 flex flex-wrap justify-end gap-2">
            <SendToArranger song={sheet} />
            <button
              type="button"
              onClick={onDownload}
              className="flex min-h-12 items-center gap-2 rounded-full border border-white/15
                px-5 text-base font-black hover:border-[color:var(--glow)]"
            >
              <Download className="h-5 w-5" /> Download .txt
            </button>
            <button
              type="button"
              onClick={() => window.print()}
              className="flex min-h-12 items-center gap-2 rounded-full border border-white/15
                px-5 text-base font-black hover:border-[color:var(--glow)]"
            >
              <Printer className="h-5 w-5" /> Print
            </button>
          </div>
        </main>

        <RhymeBrain
          color={color}
          focusedLine={focusedLine}
          lookupTrigger={lookupTrigger}
          onFirstLookup={() => {}}
          onSwap={swapFocusedLine}
          requestLookup={lookupRhymes}
        />
      </div>

      {deletedLine && (
        <div className="fixed bottom-4 left-3 z-30 flex min-h-12 items-center gap-3 rounded-full
          border border-white/15 bg-[#111415] px-4 text-base font-bold shadow-2xl"
        >
          Line deleted
          <button
            type="button"
            onClick={undoDelete}
            className="flex min-h-11 items-center gap-2 rounded-full px-3 font-black"
            style={{ color }}
          >
            <Undo2 className="h-4 w-4" /> Undo
          </button>
        </div>
      )}
    </div>
  );
};

export default BoothEditor;
